import { useEffect, useRef } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import styled from "styled-components";
import moment from "moment";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import CottageIcon from "@mui/icons-material/Cottage";
import { ITrip, ITripItem } from "@interfaces";
import { computeWeekBandSegments } from "src/Utilities/tripPlannerHelpers";
import { TripItemCard } from "./TripItemCard";

// ---- Styled --------------------------------------------------------------

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
`;

// ---- Mobile (vertical day rows) ----------------------------------------

const MobileWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const MobileDayRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    border: 1px solid #eee;
    border-radius: 8px;
    padding: 8px;
    background: #fafafa;
`;

const MobileDayHeader = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    font-size: 13px;
    font-weight: 600;
    color: #444;
`;

const MobileDayDate = styled.span`
    font-size: 11px;
    font-weight: 400;
    color: #888;
`;

const WeekLabel = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 6px;
`;

const WeekHeaderRow = styled.div`
    display: grid;
    /* minmax(0, 1fr) instead of 1fr: a bare 1fr has an implicit auto
       minimum, so a dropped tile's content can force its column wider than
       its siblings. Pinning the minimum to 0 keeps all 7 day columns exactly
       equal regardless of content. */
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 6px;
`;

const WeekDayName = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    text-align: center;
    padding: 4px 0;
`;

const WeekGrid = styled.div`
    display: grid;
    /* See WeekHeaderRow — minmax(0, 1fr) keeps every day column equal width so
       a dropped product can't stretch its column wider than the rest. */
    grid-template-columns: repeat(7, minmax(0, 1fr));
    column-gap: 8px;
    row-gap: 4px;
`;

const ColumnHeader = styled.div<{ $placeholder?: boolean }>`
    background: ${(p) => (p.$placeholder ? "transparent" : "#fafafa")};
    border: 1px solid ${(p) => (p.$placeholder ? "transparent" : "#eee")};
    border-radius: 6px;
    padding: 6px 4px;
    text-align: center;
    font-size: 12px;
    color: #555;
    font-weight: 600;
    visibility: ${(p) => (p.$placeholder ? "hidden" : "visible")};
`;

const DayBody = styled.div<{ $over: boolean; $placeholder?: boolean }>`
    border: 1px dashed ${(p) => (p.$placeholder ? "transparent" : p.$over ? "#1976d2" : "#ddd")};
    border-radius: 6px;
    padding: 8px;
    min-height: 180px;
    /* Allow the drop zone to shrink below its content's intrinsic width so the
       grid column stays fixed and the tile content wraps instead. */
    min-width: 0;
    background: ${(p) => (p.$placeholder ? "transparent" : p.$over ? "#e3f2fd" : "#fff")};
    display: flex;
    flex-direction: column;
    gap: 6px;
    transition: background 120ms ease, border-color 120ms ease;
    visibility: ${(p) => (p.$placeholder ? "hidden" : "visible")};
`;

const Band = styled.div<{
    $status: ITripItem["availabilityStatus"];
    $isStart: boolean;
    $isEnd: boolean;
    $dragging: boolean;
}>`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    background: ${(p) =>
        p.$status === "unavailable"
            ? "#ffebee"
            : p.$status === "available"
              ? "#e8f5e9"
              : "#e3f2fd"};
    border: 1px solid
        ${(p) =>
            p.$status === "unavailable"
                ? "#e57373"
                : p.$status === "available"
                  ? "#81c784"
                  : "#90caf9"};
    border-top-left-radius: ${(p) => (p.$isStart ? "12px" : "0")};
    border-bottom-left-radius: ${(p) => (p.$isStart ? "12px" : "0")};
    border-top-right-radius: ${(p) => (p.$isEnd ? "12px" : "0")};
    border-bottom-right-radius: ${(p) => (p.$isEnd ? "12px" : "0")};
    border-left-width: ${(p) => (p.$isStart ? "1px" : "0")};
    border-right-width: ${(p) => (p.$isEnd ? "1px" : "0")};
    font-size: 12px;
    color: #1a1a1a;
    cursor: grab;
    user-select: none;
    overflow: hidden;
    opacity: ${(p) => (p.$dragging ? 0.5 : 1)};
    min-height: 28px;
    box-sizing: border-box;
`;

const BandText = styled.div`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
`;

// ---- Helpers --------------------------------------------------------------

// Week starts on Sunday (US convention) per product requirement —
// the calendar header and the leading-cell padding both follow this order.
const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const StatusIcon = ({ status }: { status: ITripItem["availabilityStatus"] }) => {
    if (status === "checking") return <HourglassTopIcon sx={{ fontSize: 14, color: "#888" }} />;
    if (status === "available") return <CheckCircleIcon sx={{ fontSize: 14, color: "#43a047" }} />;
    if (status === "unavailable") return <WarningAmberIcon sx={{ fontSize: 14, color: "#e53935" }} />;
    return null;
};

// ---- Subcomponents -------------------------------------------------------

const PlaceholderCell = () => (
    <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <ColumnHeader $placeholder>.</ColumnHeader>
        <DayBody $over={false} $placeholder />
    </div>
);

interface SpanBandBodyProps {
    item: ITripItem;
    isStart: boolean;
    isEnd: boolean;
    onRemove: (id: string) => void;
    onResize?: (id: string, newDurationDays: number) => void;
    onItemClick?: (id: string) => void;
    columnPxRef?: React.MutableRefObject<number>;
}

const SpanBandBody = ({
    item,
    isStart,
    isEnd,
    onRemove,
    onResize,
    columnPxRef,
}: SpanBandBodyProps) => {
    /**
     * Right-edge resize handle. We snap to whole-day increments by measuring
     * pointer X delta against the captured column width. Stop propagation so
     * dnd-kit's draggable doesn't treat this as a drag.
     */
    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!onResize || !columnPxRef?.current) return;
        e.stopPropagation();
        e.preventDefault();
        const startX = e.clientX;
        const startSpan = item.durationDays;
        const colPx = columnPxRef.current || 1;
        const onMove = (ev: PointerEvent) => {
            const deltaCols = Math.round((ev.clientX - startX) / colPx);
            const next = Math.max(1, startSpan + deltaCols);
            if (next !== item.durationDays) onResize(item.id, next);
        };
        const onUp = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
    };
    return (
        <>
            {isStart && (
                <CottageIcon
                    data-testid="overnight-stay-icon"
                    sx={{ fontSize: 18, color: "#5d4037", flexShrink: 0 }}
                />
            )}
            <StatusIcon status={item.availabilityStatus} />
            <BandText>
                {isStart
                    ? `${item.name} · ${item.durationDays - 1} ${
                          item.durationDays - 1 === 1 ? "night" : "nights"
                      }`
                    : `${item.name} (cont.)`}
            </BandText>
            {isEnd && (
                <IconButton
                    size="small"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(item.id);
                    }}
                    sx={{ p: 0.25 }}
                >
                    <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
            )}
            {isEnd && onResize && (
                <div
                    role="separator"
                    aria-label={`Resize ${item.name}`}
                    onPointerDown={onPointerDown}
                    style={{
                        width: 8,
                        height: 24,
                        marginLeft: 2,
                        cursor: "ew-resize",
                        borderRadius: 3,
                        background: "rgba(0,0,0,0.18)",
                        flexShrink: 0,
                    }}
                />
            )}
        </>
    );
};

interface SpanBandProps extends SpanBandBodyProps {
    style?: React.CSSProperties;
    onResize?: (id: string, newDurationDays: number) => void;
    columnPxRef?: React.MutableRefObject<number>;
}

/**
 * Draggable band — registered with dnd-kit. Used ONLY for the segment that
 * contains the item's true first day. Continuation segments must use
 * `SpanBandContinuation` to avoid registering the same draggable id twice
 * (dnd-kit's `draggableNodes` map would clobber the original entry even when
 * the second hook is `disabled`).
 */
const SpanBand = ({
    item,
    isStart,
    isEnd,
    onRemove,
    style,
    onResize,
    onItemClick,
    columnPxRef,
}: SpanBandProps) => {
    const draggable = useDraggable({
        id: `item:${item.id}`,
        data: { kind: "item", itemId: item.id },
    });
    const transform = draggable.transform
        ? `translate3d(${draggable.transform.x}px, ${draggable.transform.y}px, 0)`
        : undefined;
    return (
        <Band
            ref={draggable.setNodeRef}
            $status={item.availabilityStatus}
            $isStart={isStart}
            $isEnd={isEnd}
            $dragging={draggable.isDragging}
            style={{ ...style, transform }}
            {...draggable.listeners}
            {...draggable.attributes}
            onClick={(e) => {
                if (draggable.isDragging) return;
                e.stopPropagation();
                onItemClick?.(item.id);
            }}
            title={item.availabilityMessage || item.name}
        >
            <SpanBandBody
                item={item}
                isStart={isStart}
                isEnd={isEnd}
                onRemove={onRemove}
                onResize={onResize}
                columnPxRef={columnPxRef}
            />
        </Band>
    );
};

/** Visual-only continuation band for items spanning multiple weeks. */
const SpanBandContinuation = ({
    item,
    isStart,
    isEnd,
    onRemove,
    style,
    onResize,
    onItemClick,
    columnPxRef,
}: SpanBandProps) => (
    <Band
        $status={item.availabilityStatus}
        $isStart={isStart}
        $isEnd={isEnd}
        $dragging={false}
        style={{ ...style, cursor: "pointer" }}
        onClick={(e) => {
            e.stopPropagation();
            onItemClick?.(item.id);
        }}
        title={item.availabilityMessage || item.name}
    >
        <SpanBandBody
            item={item}
            isStart={isStart}
            isEnd={isEnd}
            onRemove={onRemove}
            onResize={onResize}
            columnPxRef={columnPxRef}
        />
    </Band>
);

// ---- Main grid -----------------------------------------------------------

interface Props {
    trip: ITrip;
    onRemoveItem: (id: string) => void;
    onResizeItem?: (id: string, newDurationDays: number) => void;
    onItemClick?: (id: string) => void;
}

export const CalendarGrid = ({ trip, onRemoveItem, onResizeItem, onItemClick }: Props) => {
    // Captured column width — we measure once via a ref so the resize handle's
    // pointermove listener can convert pixel deltas into whole-day steps.
    const weekGridRef = useRef<HTMLDivElement | null>(null);
    const columnPxRef = useRef<number>(0);
    useEffect(() => {
        const measure = () => {
            const el = weekGridRef.current;
            if (!el) return;
            columnPxRef.current = el.getBoundingClientRect().width / 7;
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [trip.durationDays]);
    // Below the md breakpoint switch to a single-column vertical day list —
    // the 7-column grid overflows a 375px viewport and resize handles don't
    // make sense on touch.
    const isMobile = useMediaQuery("(max-width: 899px)");
    const start = moment(trip.startDate, "YYYY-MM-DD");

    if (isMobile) {
        return (
            <MobileCalendar
                trip={trip}
                start={start}
                onRemoveItem={onRemoveItem}
                onItemClick={onItemClick}
            />
        );
    }
    // Sunday=0 ... Saturday=6 so weeks visually align Sun→Sat (US locale).
    // `moment().day()` already returns 0 for Sunday through 6 for Saturday.
    const startWeekday = start.day();
    const totalCells = startWeekday + trip.durationDays;
    const weekCount = Math.max(1, Math.ceil(totalCells / 7));

    // Split items: single-day items go inside their day cell; multi-day items
    // become spanning bands rendered above the day cells.
    const singleByDay = new Map<number, ITripItem[]>();
    const multi: ITripItem[] = [];
    for (const it of trip.items) {
        if (it.durationDays > 1) {
            multi.push(it);
        } else {
            const arr = singleByDay.get(it.dayIndex) ?? [];
            arr.push(it);
            singleByDay.set(it.dayIndex, arr);
        }
    }

    return (
        <Wrapper>
            <WeekHeaderRow>
                {WEEKDAY_NAMES.map((d) => (
                    <WeekDayName key={d}>{d}</WeekDayName>
                ))}
            </WeekHeaderRow>

            {Array.from({ length: weekCount }).map((__, weekIdx) => {
                // dayIndex range covered by this week's cells. Negative means
                // pre-trip blank cells; >= durationDays means post-trip blanks.
                const weekFirstCellDayIdx = weekIdx * 7 - startWeekday;
                const weekLastCellDayIdx = weekFirstCellDayIdx + 6;
                const realWeekStart = Math.max(0, weekFirstCellDayIdx);
                const realWeekEnd = Math.min(trip.durationDays - 1, weekLastCellDayIdx);
                if (realWeekStart > realWeekEnd) return null;

                // Compute spanning band segments for this week.
                const segments = computeWeekBandSegments(
                    multi,
                    weekFirstCellDayIdx,
                    trip.durationDays,
                );

                const firstDay = start.clone().add(realWeekStart, "days");
                // Layout rows inside the WeekGrid:
                //   Row 1            -> column headers
                //   Rows 2..N+1      -> N spanning bands (one per row, no packing)
                //   Row N+2          -> day-body drop zones
                const headerRow = 1;
                const bandStartRow = 2;
                const bodyRow = 2 + segments.length;

                return (
                    <div key={weekIdx}>
                        <WeekLabel>
                            Week {weekIdx + 1} · starting {firstDay.format("MMM D")}
                        </WeekLabel>
                        <WeekGrid ref={weekIdx === 0 ? weekGridRef : undefined}>
                            {/* Headers + day bodies (drop targets). */}
                            {Array.from({ length: 7 }).map((_, c) => {
                                const dayIdx = weekFirstCellDayIdx + c;
                                const inRange = dayIdx >= 0 && dayIdx < trip.durationDays;
                                if (!inRange) {
                                    return (
                                        <div
                                            key={`blank-${c}`}
                                            style={{
                                                gridColumn: c + 1,
                                                gridRowStart: headerRow,
                                                gridRowEnd: bodyRow + 1,
                                            }}
                                        >
                                            <PlaceholderCell />
                                        </div>
                                    );
                                }
                                const date = start.clone().add(dayIdx, "days");
                                return (
                                    <div
                                        key={dayIdx}
                                        style={{
                                            gridColumn: c + 1,
                                            display: "contents",
                                        }}
                                    >
                                        <div
                                            style={{
                                                gridColumn: c + 1,
                                                gridRow: headerRow,
                                            }}
                                        >
                                            <ColumnHeader>
                                                <div>Day {dayIdx + 1}</div>
                                                <div
                                                    style={{
                                                        fontWeight: 400,
                                                        color: "#888",
                                                        fontSize: 11,
                                                    }}
                                                >
                                                    {date.format("MMM D")}
                                                </div>
                                            </ColumnHeader>
                                        </div>
                                        <div
                                            style={{
                                                gridColumn: c + 1,
                                                gridRow: bodyRow,
                                            }}
                                        >
                                            <DayCellDropZone
                                                dayIndex={dayIdx}
                                                items={singleByDay.get(dayIdx) ?? []}
                                                onRemoveItem={onRemoveItem}
                                                onItemClick={onItemClick}
                                            />
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Spanning bands. Each item's first segment hosts
                                the drag source; continuation segments in this
                                same week (rare for weekly multi-day items, but
                                possible if logic changes) are visual-only. */}
                            {segments.map((seg, i) => {
                                const Comp = seg.isStart ? SpanBand : SpanBandContinuation;
                                return (
                                    <Comp
                                        key={`${seg.item.id}-w${weekIdx}-r${i}`}
                                        item={seg.item}
                                        isStart={seg.isStart}
                                        isEnd={seg.isEnd}
                                        onRemove={onRemoveItem}
                                        onResize={onResizeItem}
                                        onItemClick={onItemClick}
                                        columnPxRef={columnPxRef}
                                        style={{
                                            gridColumnStart: seg.colStart,
                                            gridColumnEnd: seg.colStart + seg.colSpan,
                                            gridRow: bandStartRow + i,
                                        }}
                                    />
                                );
                            })}
                        </WeekGrid>
                    </div>
                );
            })}
        </Wrapper>
    );
};

// Inner drop body without its own column header (the header is rendered as a
// sibling grid cell so spanning bands can sit between header and body).
const DayCellDropZone = ({
    dayIndex,
    items,
    onRemoveItem,
    onItemClick,
}: {
    dayIndex: number;
    items: ITripItem[];
    onRemoveItem: (id: string) => void;
    onItemClick?: (id: string) => void;
}) => {
    const { isOver, setNodeRef } = useDroppable({
        id: `day:${dayIndex}`,
        data: { kind: "day", dayIndex },
    });
    return (
        <DayBody ref={setNodeRef} $over={isOver}>
            {items.length === 0 && (
                <div style={{ color: "#bbb", fontSize: 12, textAlign: "center", padding: 12 }}>
                    Drop here
                </div>
            )}
            {items.map((item) => (
                <TripItemCard
                    key={item.id}
                    item={item}
                    onRemove={onRemoveItem}
                    onClick={onItemClick}
                />
            ))}
        </DayBody>
    );
};

// ---- Mobile calendar -----------------------------------------------------

interface MobileCalendarProps {
    trip: ITrip;
    start: moment.Moment;
    onRemoveItem: (id: string) => void;
    onItemClick?: (id: string) => void;
}

/**
 * Single-column day list for narrow viewports. Each day row is its own drop
 * target. Multi-day items appear in every day they cover with a small
 * "(cont.)" / "(end)" hint so users can still see continuity without needing
 * the spanning grid bands. Resize is intentionally omitted on touch — users
 * change duration via the item edit modal instead.
 */
const MobileCalendar = ({ trip, start, onRemoveItem, onItemClick }: MobileCalendarProps) => {
    // Build a lookup so each day shows both its single-day items and any
    // multi-day items that overlap it.
    const itemsByDay = new Map<number, Array<{ item: ITripItem; segment: "single" | "start" | "mid" | "end" }>>();
    for (const it of trip.items) {
        if (it.durationDays <= 1) {
            const arr = itemsByDay.get(it.dayIndex) ?? [];
            arr.push({ item: it, segment: "single" });
            itemsByDay.set(it.dayIndex, arr);
            continue;
        }
        for (let offset = 0; offset < it.durationDays; offset++) {
            const day = it.dayIndex + offset;
            if (day < 0 || day >= trip.durationDays) continue;
            const segment: "start" | "mid" | "end" =
                offset === 0 ? "start" : offset === it.durationDays - 1 ? "end" : "mid";
            const arr = itemsByDay.get(day) ?? [];
            arr.push({ item: it, segment });
            itemsByDay.set(day, arr);
        }
    }
    return (
        <MobileWrapper>
            {Array.from({ length: trip.durationDays }).map((_, dayIdx) => {
                const date = start.clone().add(dayIdx, "days");
                const entries = itemsByDay.get(dayIdx) ?? [];
                return (
                    <MobileDayRow key={dayIdx}>
                        <MobileDayHeader>
                            <span>Day {dayIdx + 1}</span>
                            <MobileDayDate>{date.format("ddd, MMM D")}</MobileDayDate>
                        </MobileDayHeader>
                        <MobileDayDropZone
                            dayIndex={dayIdx}
                            entries={entries}
                            onRemoveItem={onRemoveItem}
                            onItemClick={onItemClick}
                        />
                    </MobileDayRow>
                );
            })}
        </MobileWrapper>
    );
};

const MobileDayDropZone = ({
    dayIndex,
    entries,
    onRemoveItem,
    onItemClick,
}: {
    dayIndex: number;
    entries: Array<{ item: ITripItem; segment: "single" | "start" | "mid" | "end" }>;
    onRemoveItem: (id: string) => void;
    onItemClick?: (id: string) => void;
}) => {
    const { isOver, setNodeRef } = useDroppable({
        id: `day:${dayIndex}`,
        data: { kind: "day", dayIndex },
    });
    return (
        <DayBody ref={setNodeRef} $over={isOver}>
            {entries.length === 0 && (
                <div style={{ color: "#bbb", fontSize: 12, textAlign: "center", padding: 12 }}>
                    Drop here
                </div>
            )}
            {entries.map(({ item, segment }) =>
                segment === "single" || segment === "start" ? (
                    <TripItemCard
                        key={`${item.id}-${segment}`}
                        item={item}
                        onRemove={onRemoveItem}
                        onClick={onItemClick}
                    />
                ) : (
                    <Band
                        key={`${item.id}-${segment}`}
                        $status={item.availabilityStatus}
                        $isStart
                        $isEnd
                        $dragging={false}
                        onClick={(e) => {
                            e.stopPropagation();
                            onItemClick?.(item.id);
                        }}
                        title={item.availabilityMessage || item.name}
                        style={{ cursor: "pointer" }}
                    >
                        <CottageIcon
                            data-testid="overnight-stay-icon"
                            sx={{ fontSize: 18, color: "#5d4037", flexShrink: 0 }}
                        />
                        <StatusIcon status={item.availabilityStatus} />
                        <BandText>
                            {item.name} {segment === "end" ? "(end)" : "(cont.)"}
                        </BandText>
                    </Band>
                ),
            )}
        </DayBody>
    );
};
